/**
 * Inline editing on the Templates board — the Search Results grid's machinery with the
 * one rule templates add.
 *
 * Everything structural is shared and unchanged: which columns are offered comes from
 * the backend (`GET /orders/change-all/grid-fields?binding=templates` — the
 * `is_mass_editable` flags crossed with the template field policy), edits are staged
 * rather than written, keyed by scope, and saved through the same endpoint. See
 * `../searchResults/orderGridEdit`.
 *
 * What templates add is that editability is per RECORD, not just per field. A State-2
 * field is open while a template is unapproved and locked once it is approved, and the
 * board can show both at once — its default category is Approved. So the question
 * "can this be edited" has to be asked of the row, not only of the column:
 *
 *   canEditColumn   asked of the COLUMN, for the menu's edit toggle and the drag
 *                   affordance. Answered at its most permissive (as if unapproved), or
 *                   a grid holding one unapproved template would offer nothing.
 *   cell meta       asked of the ROW. An approved row's State-2 cell renders as plain
 *                   text beside an unapproved row's editor.
 *
 * The server re-checks every record's approval on apply, so a stale row (approved by
 * someone else since the grid loaded) is refused by name rather than written.
 */
import type { BulkEditMeta } from '../../components/TanTable';
import {
  gridEditMetaFor,
  specFor,
  type GridFieldMap,
  type GridFieldSpec,
} from '../searchResults/orderGridEdit';

/**
 * Is this template row approved?
 *
 * Reads `approveddatetime`, the column the board's own category split keys on — it and
 * `approvedby` are written together and never disagree across the whole table, and
 * both views already select them. A zero date is MySQL's blank, and it arrives as the
 * literal '0000-00-00 00:00:00' rather than null, so it has to be tested for as well
 * as the empty string.
 */
export function templateRowIsApproved(row: Record<string, unknown> | undefined): boolean {
  const raw = String(row?.approveddatetime ?? '').trim();
  if (!raw || raw.startsWith('0000-00-00')) {
    // Fall back to approvedby for a row that carries one and not the other — a shape
    // that does not occur in the data today, but treating a stamped approver as
    // unapproved would open a locked field.
    return String(row?.approvedby ?? '').trim() !== '';
  }
  return true;
}

/**
 * The editor for a template grid column, or null when it may not be edited.
 *
 * `isApproved` is the RECORD's state. Pass false to ask the most permissive form of
 * the question (is this column ever editable here), which is what the column menu and
 * the drag affordance need.
 */
export function templateGridEditMetaFor(
  columnId: string,
  fields: GridFieldMap,
  opts: { isApproved: boolean; templatesDisabled?: boolean },
): BulkEditMeta | null {
  const spec = specFor(columnId, fields);
  if (!spec) return null;
  // State 2 = editable only while unapproved. State 3 (or a field published without a
  // state, which the templates binding does not do) is unconditional.
  if (spec.templateState === 2 && opts.isApproved) return null;
  // Order and main-invoice edits are both keyed by pronumber, so a template on two
  // rows makes them unplaceable in exactly the way a duplicated order does. There is
  // no third scope here — the templates binding publishes no leg or accessorial
  // fields — so this disables everything or nothing.
  if (opts.templatesDisabled) return null;
  // `allowedComponents` is deliberately omitted, which makes any field carrying a
  // second component gate (Railbill, the weight group) fail closed. None of them are
  // in the template policy today; if one is ever added it must be granted explicitly
  // rather than inherited.
  return gridEditMetaFor(columnId, fields, {});
}

/**
 * Rows a State-2 field may be dragged across — the unapproved ones.
 *
 * A drag-select stages a range in one gesture, and an approved template in the middle
 * of it must not be staged: the cell it would stage is one the grid is rendering as
 * read-only, so the user never saw an editor there.
 */
export function draggableRowsFor<T extends Record<string, unknown>>(
  spec: GridFieldSpec,
  rows: readonly T[],
): T[] {
  if (spec.templateState !== 2) return [...rows];
  return rows.filter((row) => !templateRowIsApproved(row));
}
